/**
 * Fleet revenue (TMMPDM-0010 / 0020): GET /v1/admin/revenue for the selected period + reseller.
 * TMMPDM-0037: the reseller's margin and net replace uplift and platform share (no revenue share).
 * TMMPDM-0035: `period` and `reseller` live in the URL; the scope and "Loaded at" lines sit under
 * the heading.
 */
import { useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useUrlState } from '@/hooks/useUrlState';
import { ErrorState, PageHeader } from '@/components/page';
import { periodFor, type PeriodKey } from './dashboard/period';
import { PERIOD_KEYS, PERIOD_VIEW, loadedAtLabel, periodScope } from './dashboard/periodView';
import { useRevenue } from './revenue/useRevenue';
import { RevenueTotals } from './revenue/RevenueTotals';
import { RevenueByTenantTable } from './revenue/RevenueByTenantTable';
import {
  resellerFilterOptions,
  resellerScope,
  useResellerNames,
} from './resellers/useResellerNames';

export function Revenue() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [view, setView] = useUrlState(PERIOD_VIEW);
  const { period: periodKey, reseller: resellerId } = view;
  const period = useMemo(() => periodFor(periodKey), [periodKey]);
  const { resellers, names: resellerNames } = useResellerNames();
  const { data, loading, error, failure, refetch } = useRevenue(period, resellerId);

  return (
    <Box>
      <PageHeader
        title={t('page.revenue.title', 'Revenue')}
        subtitle={t(
          'page.revenue.subtitle',
          "What the fleet collected, our wholesale price, and each reseller's margin and net.",
        )}
        scope={periodScope(
          t,
          i18n.language,
          periodKey,
          period,
          resellerScope(t, resellerNames, resellerId),
        )}
        meta={
          data ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {loadedAtLabel(t, i18n.language, data.loadedAt)}
            </Typography>
          ) : null
        }
        controls={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="revenue-reseller-label">
                {t('partners.singular', 'Partner')}
              </InputLabel>
              <Select
                labelId="revenue-reseller-label"
                value={resellerId === undefined ? 'ALL' : String(resellerId)}
                label={t('partners.singular', 'Partner')}
                onChange={(event) =>
                  setView({
                    reseller: event.target.value === 'ALL' ? undefined : Number(event.target.value),
                  })
                }
              >
                <MenuItem value="ALL">{t('dashboard.resellerFilter.all', 'All')}</MenuItem>
                {resellerFilterOptions(t, resellers, resellerId).map((reseller) => (
                  <MenuItem key={reseller.id} value={String(reseller.id)}>
                    {reseller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="revenue-period-label">
                {t('dashboard.period.label', 'Period')}
              </InputLabel>
              <Select
                labelId="revenue-period-label"
                value={periodKey}
                label={t('dashboard.period.label', 'Period')}
                onChange={(event) => setView({ period: event.target.value as PeriodKey })}
              >
                {PERIOD_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`dashboard.period.${key}`, key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      />

      {error ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={failure}
            message={t('revenue.loadError', 'Could not load revenue.')}
            onRetry={refetch}
          />
        </Box>
      ) : null}

      <RevenueTotals
        totals={data?.report.totals ?? null}
        locale={i18n.language}
        loading={loading}
      />

      <Box sx={{ mt: 3 }}>
        <RevenueByTenantTable data={data} loading={loading} locale={i18n.language} />
      </Box>
    </Box>
  );
}
